import { useEffect, useRef } from "react";

/**
 * 확인형 모달의 키보드 동작. Enter = 확인, ESC = 닫기.
 *
 * jspreadsheet 가 document 의 bubble 단계 keydown 으로 두 키를 가져가므로, capture 로 먼저 잡아
 * 전파를 끊는다.
 * - Enter: 이 모달 안 버튼에 포커스가 있으면 그 버튼의 click 에 맡긴다 (취소에 포커스 + Enter = 취소).
 * - ESC: 닫기는 ModalStack 의 Radix 가 한다. 같은 document capture 리스너라 stopPropagation 으로는
 *   막히지 않으므로, 여기서 popModal 을 또 부르면 등록 순서에 따라 모달이 두 개 닫힌다.
 *
 * 반환한 ref 는 모달 루트(ModalWrapper)에 단다. "이 모달 안"을 판정하는 기준이다.
 */
export const useConfirmModalKeys = (onConfirm: () => void) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== "Escape") return;
      e.stopPropagation();
      if (e.key === "Escape") return;
      // 이 모달 밖 버튼은 제외한다. ModalStack 이 onOpenAutoFocus 를 막아 포커스가 모달을 연 버튼
      // (예: 태그 삭제)에 남아 있고, 그 click 에 맡기면 모달이 한 번 더 열린다. 아래쪽 스택 모달이나
      // Popover 도 role="dialog" 라 "아무 dialog 안"으로는 걸러지지 않는다.
      if (
        e.target instanceof HTMLButtonElement &&
        modalRef.current?.contains(e.target)
      )
        return;
      e.preventDefault();
      onConfirmRef.current();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  return modalRef;
};
